import { useMarketdataApiService } from '@/modules/marketdata/services/marketdata-api.service';
import { BenchmarkPrice } from '@/modules/marketdata/types/marketdata';
import { Benchmark } from '@/utils/api/loans';
import Decimal from 'decimal.js';
import { ComputedRef, ShallowRef, computed, shallowRef, watch, watchEffect } from 'vue';

const benchmarks = shallowRef<BenchmarkPrice[]>([]);
let isLoaded = false;

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

export function useBenchmarksPrices(): ShallowRef<BenchmarkPrice[]> {
  const marketdataApiService = useMarketdataApiService();

  async function fetchWithRetry(attempt = 0) {
    try {
      const benchmarkPriceListResponse = await marketdataApiService.getBenchmarkPrices();
      benchmarks.value = benchmarkPriceListResponse.benchmarks ?? [];
      isLoaded = true;
    } catch {
      if (attempt < MAX_RETRIES) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        setTimeout(() => void fetchWithRetry(attempt + 1), delay);
      }
    }
  }

  if (!isLoaded) {
    void fetchWithRetry();
  }

  return benchmarks;
}

export function useBenchmarkPrice(benchmark: Benchmark): ComputedRef<Decimal | null> {
  const benchmarkPrices = useBenchmarksPrices();
  return computed(() => {
    const price = benchmarkPrices.value.find(
      (benchmarkPrice) => benchmarkPrice.benchmark === benchmark
    )?.price;
    return price === undefined ? null : new Decimal(price);
  });
}

export function useRate(): { fee: ShallowRef<Decimal | null>; rebate: ShallowRef<Decimal | null> } {
  const benchmarkPrice = useBenchmarkPrice(Benchmark.OBFR);

  const lastUpdatedRate = shallowRef<null | ShallowRef<Decimal | null>>(null);
  function syncRates(rate1: ShallowRef<Decimal | null>, rate2: ShallowRef<Decimal | null>) {
    // When prices are loaded, we only need to sync latest updated rate to another one
    // otherwise, we may override the latest updated rate with a `null`, for instance.
    // We use `{ flush: 'sync' }` to store the ref the very same moment it's updated
    watch(rate1, () => (lastUpdatedRate.value = rate1), { flush: 'sync' });
    watchEffect(() => {
      const price = benchmarkPrice.value;
      if (price === null || lastUpdatedRate.value !== rate1) return;
      const rate1Value = rate1.value;
      const rate2Value = rate1Value === null ? null : price.minus(rate1Value);
      if (
        rate2Value === rate2.value ||
        (rate2Value instanceof Decimal &&
          rate2.value instanceof Decimal &&
          rate2Value.equals(rate2.value))
      ) {
        return;
      }
      rate2.value = rate2Value;
    });
  }

  const fee = shallowRef<Decimal | null>(null);
  const rebate = shallowRef<Decimal | null>(null);

  syncRates(fee, rebate);
  syncRates(rebate, fee);

  return { fee, rebate };
}
